import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { core as appClientCore } from '@edf-pkg/app-client';
import { useSnackbar } from '@edf-pkg/app-main';

import { UserSignActions } from '$app-web/constants';
import useErrorHandler from '$app-web/hooks/use-error-handler';
import { URLsManager } from '$app-web/utils';

import useResetPasswordEndpoint from './use-reset-password.endpoint';
import useSignIn from './use-sign-in';

const useResetPassword = () => {
    const { handleError } = useErrorHandler();
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();

    const newPassword = useRef('');
    const username = useRef('');

    const { signIn, isLoading: isSignInLoading } = useSignIn({
        onSuccess: ({ mfaEnabled } = {}) => {
            if (mfaEnabled) {
                history.push(URLsManager.to('dashboard:auth:SIGN_IN'));
            }
        },
        actionSource: UserSignActions.ResetPassword,
    });

    const { mutate, isLoading, isSuccess, isError, ...rest } = useResetPasswordEndpoint({
        onSuccess: (result) => {
            const { email } = result;
            snackbar.showSuccess(t('pdash:activity_password_reset_successful'));
            username.current = email;
            signIn({ username: email, password: newPassword.current });
        },
        onError: (error) => {
            if (error.innerError?.data?.error === appClientCore.ERROR_CODES.PASSWORD_WAS_USED_BEFORE) {
                snackbar.showError(t('pdash:password_reset_error_password_used_before'));
            } else {
                snackbar.showError(t('pdash:activity_password_reset_error_incorrect_token'));
            }
            handleError(error);
        },
    });

    const resetPassword = (data, formikActions) => {
        newPassword.current = data.newPassword;
        mutate(
            {
                uid: data.uid,
                token: data.token,
                password: data.newPassword,
            },
            { onSettled: () => formikActions?.setSubmitting(false) }
        );
    };

    return {
        resetPassword,
        isLoading,
        isSuccess,
        isError,
        isSignInLoading,
        ...rest,
    };
};

export default useResetPassword;
